import { Student, WF_NationalIdentityNumberChange } from "@/interfaces/api";
import BaseService from "@/services/base-service";

export interface UpdateResponse {
  response: WF_NationalIdentityNumberChange;
  success: boolean;
}

class SsnService extends BaseService {
  /**
   * Fetches the student with the specified luEduPersonPrimaryId
   *
   * @param {string} luEduPersonPrimaryId - The luEduPersonPrimaryId of the student to fetch
   * @returns {Promise<Student>} A Promise with a student response
   */
  getStudentByLuEduPersonPrimaryId(
    luEduPersonPrimaryId: string
  ): Promise<Student> {
    const endpointController = "Student";
    const endpointRoute = "primaryId";
    const parameterName = "luEduPersonPrimaryId";
    const parameterArgument = luEduPersonPrimaryId;
    const queryString = `${parameterName}=${parameterArgument}`;
    const getStudentByLuEduPersonPrimaryId = `${this.baseApiUrl}api/${endpointController}/${endpointRoute}?${queryString}`;
    return this.fetch<Student>(getStudentByLuEduPersonPrimaryId);
  }

  /**
   * The `submitStudentData` function sends a POST request to submit student data.
   * @param {any} studentData - The data of the student to be submitted.
   * @returns A Promise that resolves to the response data.
   */
  async submitStudentData(
    studentData: WF_NationalIdentityNumberChange
  ): Promise<Student> {
    try {
      const endpointController = "NationalIdentityNumber";
      const endpointRoute = "socialSecurityNumber";
      const submitStudentData = `${this.baseApiUrl}api/${endpointController}/${endpointRoute}`;
      return this.fetch<Student>(submitStudentData, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(studentData),
      });
    } catch (error) {
      // Handle errors appropriately
      console.error("Error submitting student data: ", error);
      throw error;
    }
  }

  /**
   * Checks if the update of the national identity number for the student with the specified luEduPersonPrimaryId exists.
   *
   * @param {string} luEduPersonPrimaryId - The luEduPersonPrimaryId of the student to check
   * @returns {Promise<{ response: WF_NationalIdentityNumberChange | null; success: boolean }>} A Promise with the updated information
   */
  getIfUpdateIsInsertedByLuEduPersonPrimaryId(
    luEduPersonPrimaryId: string
  ): Promise<{
    response: WF_NationalIdentityNumberChange | null;
    success: boolean;
  }> {
    const endpointController = "NationalIdentityNumber";
    const endpointRoute = "updatedNationalIdentityNumber";
    const parameterName = "lucatid";
    const queryString = `${parameterName}=${encodeURIComponent(
      luEduPersonPrimaryId
    )}`;
    const getUpdateNationalIdentityNumber = `${this.baseApiUrl}api/${endpointController}/${endpointRoute}?${queryString}`;
    return this.fetch<{
      response: WF_NationalIdentityNumberChange | null;
      success: boolean;
    }>(getUpdateNationalIdentityNumber);
  }

  /**
   * Exports student data to a excel file based on the provided date range.
   *
   * @param {string} startDate - The start date of the date range for the export.
   * @param {string} endDate - The end date of the date range for the export.
   */
  async exportStudentsToExcel(
    startDate: string | null,
    endDate: string
  ): Promise<boolean> {
    const endpointController = "Download";
    const endpointRoute = "export";
    const queryString = `startDate=${startDate}&endDate=${endDate}`;
    const url = `${this.baseApiUrl}api/${endpointController}/${endpointRoute}?${queryString}`;

    return await this.fetch<boolean>(url);
  }
}

export default new SsnService();
